import { Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  ProgressMeter,
  StatCard,
  StatCardRow,
} from '@/modules/common/components/molecules/stat-card';

import { getRatioColor } from '../../../helpers/progress-color.helper';
import { ApprovalsSummary } from '../../../types/time-tracking-approvals';
import { SummaryChip } from '../shared/summary-chip';
import { SummaryTooltipRows } from '../shared/summary-tooltip-rows';
import { useSummaryFormat } from '../shared/use-summary-format';

interface TrackedVsBookedCardProps {
  summary: ApprovalsSummary;
}

/** How much of what the team was booked for has actually been tracked. No approval involved. */
const useTrackedVsBooked = (summary: ApprovalsSummary) => {
  const tracked = summary.trackedHours;
  const booked = summary.bookedHours;

  return {
    tracked,
    booked,
    ratioPct: booked > 0 ? (tracked / booked) * 100 : null,
    leftToTrack: Math.max(0, booked - tracked),
  };
};

const useTrackedVsBookedTooltip = (summary: ApprovalsSummary) => {
  const { t } = useTranslation('authenticated.timeTracking');
  const { formatHoursAmount, formatPct } = useSummaryFormat();
  const { tracked, booked, ratioPct, leftToTrack } = useTrackedVsBooked(summary);

  return (
    <SummaryTooltipRows
      title={t('approvals.summaryCards.trackedVsBooked.title')}
      rows={[
        {
          label: t('approvals.summaryCards.trackedVsBooked.trackedLabel'),
          value: formatHoursAmount(tracked),
        },
        {
          label: t('approvals.summaryCards.trackedVsBooked.bookedLabel'),
          value: formatHoursAmount(booked),
        },
        {
          label: t('approvals.summaryCards.trackedVsBooked.leftToTrackLabel'),
          value: formatHoursAmount(leftToTrack),
        },
        {
          label: t('approvals.summaryCards.trackedVsBooked.ratioLabel'),
          value:
            ratioPct === null
              ? t('approvals.summaryCards.trackedVsBooked.noValue')
              : formatPct(ratioPct),
        },
      ]}
    />
  );
};

export const TrackedVsBookedCard = ({ summary }: TrackedVsBookedCardProps) => {
  const { t } = useTranslation('authenticated.timeTracking');
  const { formatHoursAmount, formatPct } = useSummaryFormat();
  const { tracked, booked, ratioPct, leftToTrack } = useTrackedVsBooked(summary);

  return (
    <StatCard
      icon={<Clock size={16} aria-hidden />}
      title={t('approvals.summaryCards.trackedVsBooked.title')}
    >
      <StatCardRow
        label={t('approvals.summaryCards.trackedVsBooked.ratioLabel')}
        value={
          ratioPct === null ? (
            <span className="text-muted-foreground">
              {t('approvals.summaryCards.trackedVsBooked.noValue')}
            </span>
          ) : (
            <span
              className="text-base font-semibold tabular-nums"
              style={{ color: getRatioColor(ratioPct) }}
            >
              {formatPct(ratioPct)}
            </span>
          )
        }
      />
      <StatCardRow
        label={t('approvals.summaryCards.trackedVsBooked.leftToTrackLabel')}
        value={
          <span className="whitespace-nowrap tabular-nums">{formatHoursAmount(leftToTrack)}</span>
        }
      />
      <ProgressMeter
        progress={booked > 0 ? tracked / booked : null}
        filled={{
          label: t('approvals.summaryCards.trackedVsBooked.trackedLabel'),
          amount: tracked,
        }}
        track={{ label: t('approvals.summaryCards.trackedVsBooked.bookedLabel'), amount: booked }}
        formatAmount={formatHoursAmount}
      />
    </StatCard>
  );
};

export const TrackedVsBookedChip = ({ summary }: TrackedVsBookedCardProps) => {
  const { t } = useTranslation('authenticated.timeTracking');
  const { formatPct } = useSummaryFormat();
  const { ratioPct } = useTrackedVsBooked(summary);
  const tooltip = useTrackedVsBookedTooltip(summary);

  return (
    <SummaryChip
      icon={<Clock className="h-3 w-3" />}
      label={t('approvals.summaryCards.trackedVsBooked.ratioLabel')}
      value={
        ratioPct === null
          ? t('approvals.summaryCards.trackedVsBooked.noValue')
          : formatPct(ratioPct)
      }
      tooltip={tooltip}
    />
  );
};
